import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsersThree, Plus, X, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PortalTeamsPanel — admin CRUD for the requester "teams" used by
 * /portal/new-starts to reveal each other's onboarding tickets.
 * Emails are case-insensitively deduped server-side; the panel just
 * mirrors what the server returns after each save.
 */
export function PortalTeamsPanel({ isAdmin }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // id or "new"
  const [draft, setDraft] = useState({ name: "", member_emails: [] });
  const [emailInput, setEmailInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/portal-teams");
      setTeams(r.data.teams || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return null;

  const startNew = () => {
    setEditing("new");
    setDraft({ name: "", member_emails: [] });
    setEmailInput("");
  };
  const startEdit = (t) => {
    setEditing(t.id);
    setDraft({ name: t.name, member_emails: [...(t.member_emails || [])] });
    setEmailInput("");
  };
  const cancel = () => { setEditing(null); setEmailInput(""); };

  const addEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (!v) return;
    if (draft.member_emails.includes(v)) { setEmailInput(""); return; }
    setDraft({ ...draft, member_emails: [...draft.member_emails, v] });
    setEmailInput("");
  };
  const removeEmail = (e) => {
    setDraft({ ...draft, member_emails: draft.member_emails.filter((x) => x !== e) });
  };

  const save = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error("Team name required"); return; }
    try {
      const payload = { name, member_emails: draft.member_emails };
      if (editing && editing !== "new") payload.id = editing;
      await api.post("/admin/portal-teams", payload);
      toast.success(editing === "new" ? "Team created" : "Team updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${t.name}"? Members will no longer see each other's new starts.`)) return;
    try {
      await api.delete(`/admin/portal-teams/${t.id}`);
      toast.success("Team deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white space-y-3" data-testid="portal-teams-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UsersThree size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="font-heading font-semibold">Portal teams</div>
        </div>
        {editing === null && (
          <Button onClick={startNew} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 gap-1 text-xs" data-testid="portal-team-new">
            <Plus size={12} weight="bold" /> New team
          </Button>
        )}
      </div>
      <div className="text-xs text-slate-500">
        Members of a portal team can see each other's onboarding tickets on the requester portal's New Starts calendar. Emails are matched case-insensitively against the magic-link login.
      </div>

      {loading && <div className="text-xs text-slate-500 italic">Loading…</div>}

      {editing !== null && (
        <div className="border border-[#4f4c90]/30 rounded-md p-3 bg-[#4f4c90]/5 space-y-2">
          <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">
            {editing === "new" ? "New team" : "Edit team"}
          </div>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Team name (e.g. HR Onboarding)"
            className="bg-white border-slate-200 h-9"
            data-testid="portal-team-name"
          />
          <div>
            <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-1">Members</div>
            <div className="flex flex-wrap gap-1.5 min-h-6">
              {draft.member_emails.length === 0 && (
                <span className="text-[11px] text-slate-500 italic">No members yet.</span>
              )}
              {draft.member_emails.map((em) => (
                <span key={em} className="chip !text-slate-800 !border-slate-300 bg-slate-100 flex items-center gap-1" data-testid={`portal-team-member-${em}`}>
                  {em}
                  <button type="button" onClick={() => removeEmail(em)} className="hover:text-red-500" title="Remove">
                    <X size={11} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                placeholder="alice@example.co"
                className="bg-white border-slate-200 h-9 max-w-xs"
                data-testid="portal-team-email-input"
              />
              <Button onClick={addEmail} className="bg-slate-700 hover:bg-slate-800 h-9 gap-1" data-testid="portal-team-email-add">
                <Plus size={12} weight="bold" /> Add
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} className="bg-[#39A36E] hover:bg-[#2f8c5d] h-9" data-testid="portal-team-save">Save team</Button>
            <Button onClick={cancel} variant="outline" className="h-9">Cancel</Button>
          </div>
        </div>
      )}

      {teams.length === 0 && editing === null ? (
        <div className="text-[11px] text-slate-500 italic border border-dashed border-slate-200 rounded-md px-3 py-4 text-center">
          No teams yet — every requester only sees their own new starts. Click "New team" to add one.
        </div>
      ) : (
        <div className="space-y-1.5">
          {teams.map((t) => (
            <div key={t.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-slate-50/60" data-testid={`portal-team-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.name}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {(t.member_emails || []).length} member{(t.member_emails || []).length === 1 ? "" : "s"}
                  {(t.member_emails || []).length > 0 && (
                    <> · {(t.member_emails || []).slice(0, 3).join(", ")}
                      {(t.member_emails || []).length > 3 && ` +${t.member_emails.length - 3}`}
                    </>
                  )}
                </div>
              </div>
              <button type="button" onClick={() => startEdit(t)} className="text-slate-500 hover:text-[#4f4c90] p-1" title="Edit" data-testid={`portal-team-edit-${t.id}`}>
                <PencilSimple size={13} weight="duotone" />
              </button>
              <button type="button" onClick={() => remove(t)} className="text-slate-500 hover:text-red-500 p-1" title="Delete">
                <X size={13} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
